import matter from "gray-matter";

export type RemoteMessage = {
  id: string;
  startTime: string;
  endTime: string;
  dismissible: boolean;
  priority: number;
  scenario: "full-screen" | "banner";
  body: string;
  /** When false, message is hidden on live (and all non–platform-test hosts). Omitted = show on live. */
  showOnLive?: boolean;
  /** When false, message is hidden on platform-test.simpl.org. Omitted = show on test. */
  showOnTest?: boolean;
};

type ParsedMessageContents = {
  data: Record<string, unknown>;
  content: string;
};

const ISO_TIMESTAMP_REGEX = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z/;
const KEY_BOUNDARY_PATTERN =
  "(?:start[\\s_-]*time|end[\\s_-]*time|dismissible|priority|scenario)";
const KEY_NAME_REGEX = new RegExp(`^${KEY_BOUNDARY_PATTERN}\\s*:`, "i");

const normalizeKey = (key: string): string =>
  key.toLowerCase().replace(/[^a-z]/g, "");

const SUPPORTED_FRONT_MATTER_KEYS = new Set([
  "starttime",
  "endtime",
  "dismissible",
  "priority",
  "scenario",
  "showonlive",
  "showontest"
]);

const isSupportedKey = (key: string) =>
  SUPPORTED_FRONT_MATTER_KEYS.has(normalizeKey(key));

const normalizeFrontMatter = (raw: string): string => {
  const leadingWhitespaceMatch = raw.match(/^\s*/);
  const leadingWhitespace = leadingWhitespaceMatch
    ? leadingWhitespaceMatch[0]
    : "";
  const trimmed = raw.trimStart();
  if (!trimmed.startsWith("---")) {
    return raw;
  }

  const withOpeningNewline = trimmed.startsWith("---\n")
    ? trimmed
    : `---\n${trimmed.slice(3).trimStart()}`;

  let closingIndex = withOpeningNewline.indexOf("\n---", 4);
  let closingExtraOffset = 1;
  if (closingIndex === -1) {
    closingIndex = withOpeningNewline.indexOf("---", 4);
    closingExtraOffset = 0;
  }
  if (closingIndex === -1) {
    return `${leadingWhitespace}${withOpeningNewline}`;
  }

  const header = withOpeningNewline.slice(4, closingIndex).trim();
  const body = withOpeningNewline.slice(closingIndex + closingExtraOffset + 3);

  const formattedHeader = sanitizeHeader(header, raw).join("\n");

  const closingSuffix = body.startsWith("\n") ? body : `\n${body}`;
  const normalizedBody = closingSuffix.length === 0 ? "\n" : closingSuffix;

  return `${leadingWhitespace}---\n${formattedHeader}\n---${normalizedBody}`;
};

const sanitizeHeader = (header: string, raw: string): string[] => {
  const lines = header
    .replace(/\s*([A-Za-z_][\w-]*)\s*:/g, "\n$1:")
    .split("\n")
    .map((line) => line.trim());

  const sanitized = lines.filter((line) => {
    if (!line) return false;
    const colonIndex = line.indexOf(":");
    if (colonIndex === -1) return false;
    const key = line.slice(0, colonIndex).trim();
    return isSupportedKey(key);
  });

  const keysPresent = new Set(
    sanitized.map((line) => line.slice(0, line.indexOf(":")).trim())
  );

  ["start_time", "end_time"].forEach((key) => {
    if (keysPresent.has(key)) return;
    const recovered = recoverIsoValueFromRaw(
      raw,
      key as "start_time" | "end_time"
    );
    if (recovered) {
      sanitized.push(`${key}: ${recovered}`);
    }
  });

  return sanitized;
};

const parseInlineFrontMatter = (raw: string): ParsedMessageContents => {
  const trimmed = raw.trim();
  if (!trimmed.startsWith("---")) {
    return { data: {}, content: raw.trim() };
  }

  const secondDelimiterIndex = trimmed.indexOf("---", 3);
  if (secondDelimiterIndex === -1) {
    return { data: {}, content: raw.trim() };
  }

  const header = trimmed.slice(3, secondDelimiterIndex).trim();
  const content = trimmed.slice(secondDelimiterIndex + 3).trim();
  const data: Record<string, string> = {};
  const lines = sanitizeHeader(header, raw);

  lines.forEach((line) => {
    const colonIndex = line.indexOf(":");
    if (colonIndex === -1) return;
    const key = line.slice(0, colonIndex).trim();
    const value = line.slice(colonIndex + 1).trim();
    if (!value || KEY_NAME_REGEX.test(value)) return;
    if (!key || !isSupportedKey(key) || !value) return;
    data[key] = value;
  });

  return { data, content };
};

const parseFrontMatter = (raw: string): ParsedMessageContents => {
  const inlineParsed = parseInlineFrontMatter(raw);
  if (Object.keys(inlineParsed.data).length > 0) {
    return inlineParsed;
  }

  const normalized = normalizeFrontMatter(raw);
  try {
    return matter(normalized) as ParsedMessageContents;
  } catch {
    return inlineParsed;
  }
};

const tryParseJsonMessage = (raw: string): ParsedMessageContents | null => {
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const { body, ...rest } = parsed;
    return {
      data: rest,
      content: typeof body === "string" ? body : ""
    };
  } catch {
    return null;
  }
};

const parseLooseKeyValueMessage = (
  raw: string
): ParsedMessageContents | null => {
  const lines = raw.split(/\r?\n/);
  const data: Record<string, string> = {};
  let bodyStartIndex = lines.length;
  let sawKey = false;

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (!line.trim()) {
      bodyStartIndex = i + 1;
      break;
    }
    const colonIndex = line.indexOf(":");
    if (colonIndex === -1) {
      bodyStartIndex = i;
      break;
    }
    const key = line.slice(0, colonIndex).trim();
    const value = line.slice(colonIndex + 1).trim();
    if (!key) {
      continue;
    }
    if (!isSupportedKey(key)) {
      bodyStartIndex = i;
      break;
    }
    if (!value || KEY_NAME_REGEX.test(value)) {
      data[key] = "";
      continue;
    }
    data[key] = value;
    sawKey = true;
  }

  if (!sawKey) {
    return null;
  }

  const content = lines.slice(bodyStartIndex).join("\n").trim();
  return { data, content };
};

const recoverIsoValueFromRaw = (
  raw: string,
  key: "start_time" | "end_time"
): string => {
  const normalizedKeyPattern =
    key === "start_time" ? "start[\\s_-]*time" : "end[\\s_-]*time";
  const pattern = new RegExp(
    `${normalizedKeyPattern}\\s*:\\s*([\\s\\S]*?)(?=${KEY_BOUNDARY_PATTERN}|---|$)`,
    "i"
  );
  const match = pattern.exec(raw);
  if (!match) {
    return "";
  }
  const isoMatch = match[1].match(ISO_TIMESTAMP_REGEX);
  return isoMatch ? isoMatch[0] : "";
};

const isValidIsoDate = (value: string): boolean => {
  if (!value) return false;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp);
};

const coerceString = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }
  if (value === undefined || value === null) {
    return "";
  }
  return String(value);
};

const coerceBoolean = (value: unknown): boolean => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    return value.toLowerCase() === "true";
  }
  return Boolean(value);
};

const coerceNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === "number" && !Number.isNaN(value)) {
    return value;
  }
  const asNumber = Number(value);
  return Number.isNaN(asNumber) ? fallback : asNumber;
};

type FrontMatterAccessor = {
  get: (canonicalKey: string) => unknown;
  has: (canonicalKey: string) => boolean;
};

const createFrontMatterAccessor = (
  data: Record<string, unknown>
): FrontMatterAccessor => {
  const lookup = new Map<string, string>();
  Object.keys(data).forEach((key) => {
    if (!isSupportedKey(key)) {
      return;
    }
    const normalized = normalizeKey(key);
    if (!lookup.has(normalized)) {
      lookup.set(normalized, key);
    }
  });

  return {
    get: (canonicalKey: string) => {
      const actualKey = lookup.get(normalizeKey(canonicalKey));
      return actualKey ? data[actualKey] : undefined;
    },
    has: (canonicalKey: string) => lookup.has(normalizeKey(canonicalKey))
  };
};

export const parseRemoteMessage = (
  key: string,
  raw: string
): RemoteMessage | null => {
  let parsed = parseFrontMatter(raw);
  let accessor = createFrontMatterAccessor(parsed.data);

  const resolveWindow = () => {
    const startTimeRaw = accessor.get("start_time");
    const endTimeRaw = accessor.get("end_time");
    return {
      startTime: coerceString(startTimeRaw),
      endTime: coerceString(endTimeRaw)
    };
  };

  let { startTime, endTime } = resolveWindow();

  if ((!startTime || !endTime) && raw.trim().startsWith("{")) {
    const jsonParsed = tryParseJsonMessage(raw);
    if (jsonParsed) {
      parsed = jsonParsed;
      accessor = createFrontMatterAccessor(parsed.data);
      ({ startTime, endTime } = resolveWindow());
    }
  }

  if (!startTime || !endTime) {
    const looseParsed = parseLooseKeyValueMessage(raw);
    if (looseParsed) {
      parsed = looseParsed;
      accessor = createFrontMatterAccessor(parsed.data);
      ({ startTime, endTime } = resolveWindow());
    }
  }

  if (!startTime || !ISO_TIMESTAMP_REGEX.test(startTime)) {
    const recoveredStart = recoverIsoValueFromRaw(raw, "start_time");
    if (recoveredStart) {
      startTime = recoveredStart;
    }
  }

  if (!endTime || !ISO_TIMESTAMP_REGEX.test(endTime)) {
    const recoveredEnd = recoverIsoValueFromRaw(raw, "end_time");
    if (recoveredEnd) {
      endTime = recoveredEnd;
    }
  }

  if (!isValidIsoDate(startTime) || !isValidIsoDate(endTime)) {
    return null;
  }

  const priority = coerceNumber(accessor.get("priority"));
  const scenarioKey = coerceString(accessor.get("scenario"));
  const hasDismissibleKey = accessor.has("dismissible");
  const dismissible = hasDismissibleKey
    ? coerceBoolean(accessor.get("dismissible"))
    : true;

  const scenario: RemoteMessage["scenario"] =
    scenarioKey.toLowerCase() === "banner" ? "banner" : "full-screen";

  const showOnLive = accessor.has("show_on_live")
    ? coerceBoolean(accessor.get("show_on_live"))
    : undefined;
  const showOnTest = accessor.has("show_on_test")
    ? coerceBoolean(accessor.get("show_on_test"))
    : undefined;

  return {
    id: key,
    startTime,
    endTime,
    dismissible,
    priority,
    scenario,
    body: parsed.content.trim(),
    showOnLive,
    showOnTest
  };
};


